// RB-MY01-PalmGarden.web/src/components/PlantForm/components/FormSections/BasicInfo.tsx
import React from 'react';
import { 
  TextField, 
  Typography, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Tooltip, 
  IconButton, 
  FormHelperText, 
  InputAdornment,
  Paper
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Plant } from '../../../../services/api';
import SectionCard from '../SectionCard';
import { plantTypes, lightRequirements } from '../../constants';
import { sectionThemes } from '../../styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import LightModeIcon from '@mui/icons-material/LightMode';
import ScienceIcon from '@mui/icons-material/Science';
import DescriptionIcon from '@mui/icons-material/Description';

interface BasicInfoProps {
  plant: Partial<Plant>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (event: SelectChangeEvent<string>) => void;
}

const BasicInfo: React.FC<BasicInfoProps> = ({ 
  plant, 
  handleChange, 
  handleSelectChange
}) => {
  // Check if a field has been filled
  const isFieldFilled = (fieldName: string): boolean => {
    const value = plant[fieldName as keyof typeof plant];
    return Boolean(value && (typeof value === 'string' ? value.trim() !== '' : true));
  };
  
  // Simplified field styling with a professional look
  const customFieldStyle = {
    position: 'relative',
    marginTop: '20px',
    
    '& .MuiInputBase-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      transition: 'all 0.2s',
      
      '&:hover': {
        borderColor: '#bdbdbd',
      },
      
      '&.Mui-focused': {
        borderColor: sectionThemes.basicInfo.color,
        boxShadow: `0 0 0 1px ${sectionThemes.basicInfo.color}40`,
      },
      
      // If field is filled, show a subtle success indication
      ...(isFieldFilled('name') && {
        borderColor: `${sectionThemes.basicInfo.color}`,
      }),
      
      // Remove the default Material-UI outline
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      
      // Style the input text
      '& input, & .MuiSelect-select': {
        fontSize: '0.95rem',
        padding: '12px 14px',
      },
    },
    
    // Clean label styling
    '& .MuiFormLabel-root': {
      position: 'absolute',
      top: '-10px',
      left: '10px',
      backgroundColor: 'white',
      padding: '0 5px',
      fontSize: '0.8rem',
      fontWeight: 500,
      color: 'rgba(0, 0, 0, 0.6)',
      
      '&.Mui-focused': {
        color: sectionThemes.basicInfo.color,
      },
    },
    
    // Style helper text
    '& .MuiFormHelperText-root': {
      marginLeft: '5px',
      marginTop: '5px',
      fontSize: '0.75rem',
      color: 'rgba(0, 0, 0, 0.5)',
    },
  };

  // Simple section styling
  const sectionStyle = {
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginBottom: '16px',
    backgroundColor: '#f9f9f9',
  };

  // Simple section title style
  const sectionTitleStyle = {
    fontSize: '1rem',
    fontWeight: 500,
    color: '#424242',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '8px',
    borderBottom: '1px solid #e0e0e0'
  };

  return (
    <SectionCard 
      title="Basic Plant Information" 
      icon={<LocalFloristIcon style={{ marginRight: '8px', color: sectionThemes.basicInfo.color }} />}
      theme={{ color: sectionThemes.basicInfo.color }}
    >
      {/* Main plant details */}
      <Paper 
        elevation={0} 
        sx={{ 
          ...sectionStyle,
          mb: 3
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{
            ...sectionTitleStyle,
            color: '#424242'
          }}
        >
          <DescriptionIcon sx={{ mr: 1, fontSize: '1rem', color: sectionThemes.basicInfo.color }} />
          Plant Identification
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 3,
          maxWidth: '100%',
          padding: '8px 0',
        }}>
          <Box sx={customFieldStyle}>
            <InputLabel htmlFor="plant-name" shrink={true}>
              Plant Name <span style={{ color: '#d32f2f' }}>*</span>
            </InputLabel>
            <TextField
              required
              fullWidth
              id="plant-name"
              name="name"
              value={plant.name || ''}
              onChange={handleChange}
              variant="outlined"
              placeholder="Enter plant name"
              helperText="Common or display name of the plant"
              InputLabelProps={{ shrink: true }}
              label=""
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalFloristIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={customFieldStyle}>
            <InputLabel htmlFor="plant-type" shrink={true}>
              Plant Type <span style={{ color: '#d32f2f' }}>*</span>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                id="plant-type"
                name="type"
                value={plant.type || ''}
                onChange={handleSelectChange}
                variant="outlined"
                displayEmpty
                label=""
                startAdornment={
                  <InputAdornment position="start">
                    <LocalFloristIcon fontSize="small" color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="" disabled>
                  <em style={{ color: 'rgba(0,0,0,0.4)' }}>Select plant type</em>
                </MenuItem>
                {plantTypes.map((option) => (
                  <MenuItem 
                    key={option} 
                    value={option}
                    sx={{
                      borderLeft: option === plant.type ? `2px solid ${sectionThemes.basicInfo.color}` : '2px solid transparent',
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>The primary category of this plant</FormHelperText>
            </FormControl>
          </Box>
          
          <Box sx={customFieldStyle}>
            <InputLabel htmlFor="scientific-name" shrink={true}>Scientific Name (Optional)</InputLabel>
            <TextField
              fullWidth
              id="scientific-name"
              name="scientificName"
              value={plant.scientificName || ''}
              onChange={handleChange}
              variant="outlined"
              placeholder="Enter scientific name"
              helperText="Botanical name (genus and species)"
              label=""
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScienceIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                style: { fontStyle: 'italic' }
              }}
            />
          </Box>
          
          <Box sx={customFieldStyle}>
            <InputLabel htmlFor="light-requirement" shrink={true}>
              Light Requirement <span style={{ color: '#d32f2f' }}>*</span>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                id="light-requirement"
                name="lightRequirement"
                value={plant.lightRequirement || ''}
                onChange={handleSelectChange}
                variant="outlined"
                displayEmpty
                label=""
                startAdornment={
                  <InputAdornment position="start">
                    <LightModeIcon fontSize="small" color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="" disabled>
                  <em style={{ color: 'rgba(0,0,0,0.4)' }}>Select light requirement</em>
                </MenuItem>
                {lightRequirements.map((option) => (
                  <MenuItem 
                    key={option} 
                    value={option}
                    sx={{
                      borderLeft: option === plant.lightRequirement ? `2px solid ${sectionThemes.basicInfo.color}` : '2px solid transparent',
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>How much light does this plant need?</FormHelperText>
            </FormControl>
          </Box>
        </Box>
      </Paper>
      
      {/* Description */}
      <Paper 
        elevation={0} 
        sx={{ 
          ...sectionStyle
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{
            ...sectionTitleStyle,
            color: '#424242'
          }}
        >
          <DescriptionIcon sx={{ mr: 1, fontSize: '1rem', color: sectionThemes.basicInfo.color }} />
          Description
        </Typography>
        
        <Box sx={customFieldStyle}>
          <InputLabel htmlFor="description" shrink={true}>
            Description <span style={{ color: '#d32f2f' }}>*</span>
          </InputLabel>
          <TextField
            required
            fullWidth
            id="description"
            name="description"
            value={plant.description || ''}
            onChange={handleChange}
            variant="outlined"
            multiline
            rows={3}
            placeholder="Describe your plant's characteristics and features"
            helperText="Provide a general overview of the plant"
            InputLabelProps={{ shrink: true }}
            label=""
          />
        </Box>
        
        <Box sx={{ ...customFieldStyle, mt: 3 }}>
          <InputLabel htmlFor="care-instructions" shrink={true}>
            Care Instructions <span style={{ color: '#d32f2f' }}>*</span>
          </InputLabel>
          <TextField
            required
            fullWidth
            id="care-instructions"
            name="careInstructions"
            value={plant.careInstructions || ''}
            onChange={handleChange}
            variant="outlined"
            multiline
            rows={3}
            placeholder="Provide care instructions for this plant"
            helperText="Details on watering, light, soil, and maintenance"
            InputLabelProps={{ shrink: true }}
            label=""
          />
        </Box>
      </Paper>
    </SectionCard>
  );
};

export default React.memo(BasicInfo);